import { Button } from '@mui/material';
import { useGetBillingCountsQuery } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useFlag } from '@schooly/hooks/use-flag';
import { IncompleteSvg, Loading, PlusIcon, Spin } from '@schooly/style';
import { fulfillJSDWidget } from '@schooly/utils/fulfill-jsd-widget';
import React, { FC } from 'react';
import { useIntl } from 'react-intl';
import { Link, Outlet, useNavigate, useParams } from 'react-router-dom';

import MainLayout from '../../../components/uikit-components/MainLayout/MainLayout';
import { WithFrequencies } from '../../../context/frequencies/WithFrequencies';
import useSchoolYears from '../../../hooks/useSchoolYears';
import AccessDeniedPage from '../../AccessDenied';
import { SchoolPaymentFrequenciesEmptyStub } from './SchoolPaymentFrequenciesEmptyStub';

//TR-6100 This component contains commented code that is to be used
// when full functionality of frequencies is needed to be introduced

export const SchoolPaymentFrequencies: FC = () => {
  const {
    isLoading: yearsFetching,
    getDefaultValidity,
    schoolYears,
    defaultValidity,
  } = useSchoolYears();
  const navigate = useNavigate();
  const { $t } = useIntl();
  const { schoolId = '', permissions, currentSchool, currentUser } = useAuth();

  const { yearId } = useParams<'yearId'>();
  const paramsYear = schoolYears.find((y) => y.id === yearId);

  const [isLoadingWidget, startLoading, endLoading] = useFlag(false);

  const canView =
    permissions.includes('product_and_invoice_viewer') ||
    permissions.includes('registration_manager');
  const hasSchoolAdminPermissions = permissions.includes('school_admin');

  const { data: countsData, isLoading: isCountsLoading } = useGetBillingCountsQuery(schoolId, {
    refetchOnMount: 'always',
    enabled: !!schoolId,
  });
  const hasFrequencies = Boolean(countsData?.frequencies.length);

  if (yearsFetching || isCountsLoading)
    return (
      <MainLayout>
        <Loading />
      </MainLayout>
    );

  if (!canView) {
    return <AccessDeniedPage />;
  }

  //Redirect to default school year
  if (!paramsYear && defaultValidity) {
    navigate({ pathname: `/settings/frequencies/year/${defaultValidity?.id}` }, { replace: true });
    return null;
  }

  const hasOnlyPastYears = !getDefaultValidity(true);

  const hasNoYears = hasOnlyPastYears || !defaultValidity;
  if (hasNoYears) {
    return (
      <MainLayout>
        <SchoolPaymentFrequenciesEmptyStub
          labelTextId="frequencies-NoCurrentOrFutureYears"
          action={
            hasSchoolAdminPermissions && (
              <Link to="/settings/frequencies/school_years">
                <Button startIcon={<PlusIcon />}>
                  {$t({ id: 'school-tabs-SchoolYears-AddSchoolYear' })}
                </Button>
              </Link>
            )
          }
        />
      </MainLayout>
    );
  }

  if (!hasFrequencies) {
    return (
      <MainLayout>
        {/* To be used in TR-6644 Invoices. Frequency Settings V2 */}
        {/* <SchoolPaymentFrequenciesEmptyStub
          labelTextId="frequencies-SetFrequenciesToStart"
          descriptionLabelTextId="frequencies-SetFrequenciesToStart-Description"
          action={
            canEdit && (
            <Button startIcon={<PlusIcon />}>{$t({ id: 'frequencies-SetFrequencies' })}</Button>
            )
          }
        />
        <Outlet /> */}

        <SchoolPaymentFrequenciesEmptyStub
          svg={<IncompleteSvg />}
          labelTextId="frequencies-RequestFrequencies"
          descriptionLabelTextId="frequencies-RequestFrequencies-Description"
          action={
            <Button
              onClick={async () => {
                startLoading();
                await fulfillJSDWidget('Frequencies', {
                  summary: 'Request Update to Invoicing Frequencies',
                  description: currentSchool?.name,
                  email: currentUser?.account_email,
                });
                endLoading();
              }}
              disabled={isLoadingWidget}
              startIcon={isLoadingWidget ? <Spin /> : undefined}
            >
              {$t({ id: 'action-SubmitRequest' })}
            </Button>
          }
        />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <WithFrequencies schoolYear={paramsYear}>
        <Outlet />
      </WithFrequencies>
    </MainLayout>
  );
};
